/**
 * Modal components for Heat Pump Simulation
 * Includes U-parameters modal, radiator types modal, and document resources modal
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Drawer, Space, Input, Button, Select, Spin, Empty, message } from "antd";
import { ArrowLeftOutlined, FilePdfOutlined, VideoCameraOutlined, CheckCircleFilled, EnvironmentOutlined, AimOutlined } from "@ant-design/icons";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import RadiatorTypesImage from "../../Shared/RadiatorTypes.jpg";
import FundamentalsDocument from "./HPS_Fundamentals_of_Heat_transfer.pdf";
import UValueDocument from "./HPS_U_Values.pdf";
import VentilationDocument from "./HPS_Ventilation.pdf";
import Building1960 from "./building_1960.png";
import Building1980 from "./building_1980.png";
import Building2000 from "./building_2000.png";
import Building2020 from "./building_2020.png";
import { LatexRenderer } from "../../Shared/LatexRenderer";
import styles from "./Modals.module.css";

/**
 * Common window types and their typical U-values
 * Based on standard thermal performance data
 */
const WINDOW_TYPES = [
  { label: "Single-pane", value: 5.8, description: "Basic single-layer glass" },
  { label: "Double-pane (standard)", value: 2.8, description: "Standard insulated glass unit" },
  { label: "Double-pane (Low-E)", value: 1.8, description: "Low-emissivity coating" },
  { label: "Triple-pane (standard)", value: 1.6, description: "Three layers of glass" },
  { label: "Triple-pane (Low-E, Argon)", value: 0.9, description: "Gas-filled with Low-E coating" },
  { label: "Passive House standard", value: 0.6, description: "High-performance window" },
];

const DEFAULT_LEARNING_RESOURCES = [
  { id: 'fundamentals', title: 'Fundamentals', file: FundamentalsDocument, fileExtension: '.pdf', contentType: 'application/pdf' },
  { id: 'u-values', title: 'U-values', file: UValueDocument, fileExtension: '.pdf', contentType: 'application/pdf' },
  { id: 'ventilation', title: 'Ventilation', file: VentilationDocument, fileExtension: '.pdf', contentType: 'application/pdf' },
];

const getLearningResourcePresentation = (resource) => {
  const contentType = String(resource?.contentType || '').toLowerCase();
  const extension = String(resource?.fileExtension || '').toLowerCase();

  if (contentType.startsWith('video/') || extension === '.mp4') {
    return {
      accentColor: '#c41d7f',
      badgeColor: 'magenta',
      icon: VideoCameraOutlined,
      label: 'Video',
    };
  }

  return {
    accentColor: '#cf1322',
    badgeColor: 'red',
    icon: FilePdfOutlined,
    label: 'PDF',
  };
};

/**
 * U-Parameters Configuration Modal
 * Allows detailed configuration of building envelope parameters
 */
export const UParametersModal = ({
  visible,
  onClose,
  roofArea,
  roofU,
  wallArea,
  wallU,
  windowArea,
  windowU,
  onRoofAreaChange,
  onRoofUChange,
  onWallAreaChange,
  onWallUChange,
  onWindowAreaChange,
  onWindowUChange,
  onEnvelopeUChange,
}) => {
  const { t } = useTranslation();
  
  const handleNumberInput = (value, min, max, onChange) => {
    const num = parseFloat(value);
    const clamped = Math.max(min, Math.min(max, num));
    onChange(clamped);
  };

  const handleConfirm = () => {
    const totalArea = roofArea + wallArea + windowArea;
    if (totalArea > 0) {
      let weightedU = (roofArea * roofU + wallArea * wallU + windowArea * windowU) / totalArea;
      weightedU = Math.round(weightedU * 100) / 100;
      onEnvelopeUChange(weightedU);
    }
    onClose();
  };

  return (
    <Drawer
      title={<span style={{ fontSize: '18px', fontWeight: 600, color: '#0050b3' }}>{t('heatPumpSimulation.modals.buildingEnvelope.title')}</span>}
      placement="left"
      width={'50%'}
      onClose={onClose}
      open={visible}
      closeIcon={<ArrowLeftOutlined />}
      closable={true}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Roof Parameters */}
        <Space>
          <p style={{ width: "5vw", color:"gray" }}>{t('heatPumpSimulation.modals.buildingEnvelope.roof')}</p>
          <Input
            size="small"
            type="number"
            min={0.0}
            max={100}
            step={0.1}
            value={roofArea}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>{t('heatPumpSimulation.modals.buildingEnvelope.area')}</p>}
            suffix={<LatexRenderer latex="$ m^2 $" />}
            onChange={(e) => handleNumberInput(e.target.value, 0, 100, onRoofAreaChange)}
          />
          <Input
            size="small"
            type="number"
            min={0.0}
            max={10}
            step={0.1}
            value={roofU}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>{t('heatPumpSimulation.modals.buildingEnvelope.uValue')}</p>}
            suffix={"W/m²K"}
            onChange={(e) => handleNumberInput(e.target.value, 0, 10, onRoofUChange)}
          />
        </Space>

        {/* Wall Parameters */}
        <Space>
          <p style={{ width: "5vw", color:"gray"}}>{t('heatPumpSimulation.modals.buildingEnvelope.wall')}</p>
          <Input
            size="small"
            type="number"
            min={0.0}
            max={100}
            step={0.1}
            value={wallArea}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>{t('heatPumpSimulation.modals.buildingEnvelope.area')}</p>}
            suffix={<LatexRenderer latex="$ m^2 $" />}
            onChange={(e) => handleNumberInput(e.target.value, 0, 100, onWallAreaChange)}
          />
          <Input
            size="small"
            type="number"
            min={0.0}
            max={10}
            step={0.1}
            value={wallU}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>{t('heatPumpSimulation.modals.buildingEnvelope.uValue')}</p>}
            suffix={"W/m²K"}
            onChange={(e) => handleNumberInput(e.target.value, 0, 10, onWallUChange)}
          />
        </Space>

        {/* Window Parameters */}
        <Space>
          <p style={{ width: "5vw", color:"gray" }}>{t('heatPumpSimulation.modals.buildingEnvelope.windows')}</p>
          <Input
            size="small"
            type="number"
            min={0.0}
            max={100}
            step={0.1}
            value={windowArea}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>{t('heatPumpSimulation.modals.buildingEnvelope.area')}</p>}
            suffix={<LatexRenderer latex="$ m^2 $" />}
            onChange={(e) => handleNumberInput(e.target.value, 0, 100, onWindowAreaChange)}
          />
          <Input
            size="small"
            type="number"
            min={0.0}
            max={10}
            step={0.1}
            value={windowU}
            style={{ width: "12vw", fontSize: "12px" }}
            prefix={<p className="default-gray" style={{ paddingRight: "2vw", fontSize: "11px" }}>{t('heatPumpSimulation.modals.buildingEnvelope.uValue')}</p>}
            suffix={"W/m²K"}
            onChange={(e) => handleNumberInput(e.target.value, 0, 10, onWindowUChange)}
          />
          <Select
            size="small"
            placeholder={t('heatPumpSimulation.modals.buildingEnvelope.selectWindowType')}
            style={{ width: "18vw", height: "40px" }}
            onChange={(value) => onWindowUChange(value)}
            value={WINDOW_TYPES.find(w => Math.abs(w.value - windowU) < 0.01)?.value}
            optionLabelProp="label"
            options={WINDOW_TYPES.map(type => ({
              label: `${type.label} (${type.value} W/m²K)`,
              value: type.value,
              desc: type.description,
            }))}
            optionRender={(option) => (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                <div>
                  <div style={{ fontWeight: 450, color: '#333' }}>{WINDOW_TYPES.find(w => w.value === option.value)?.label}</div>
                  <div style={{ fontSize: "11px", color: "#888" }}>{option.data.desc}</div>
                </div>
                <div style={{ 
                  fontWeight: 450, 
                  color: '#1890ff', 
                  fontSize: '13px',
                  marginLeft: '4px'
                }}>
                  {option.value} W/m²K
                </div>
              </div>
            )}
          />
        </Space>

        {/* Calculated Envelope U-Value */}
        <div style={{ 
          padding: "16px", 
          backgroundColor: "#f5f5f5", 
          borderRadius: "4px",
          marginTop: "16px",
          borderLeft: "4px solid #1890ff"
        }}>
          <p style={{ margin: "0 0 8px 0", fontWeight: "500", color: "#333" }}>
            {t('heatPumpSimulation.modals.buildingEnvelope.weightedUValue')}
          </p>
           <p style={{ margin: "12px 0 12px 0px", fontSize: "12px", color: "#666" }}>
              <LatexRenderer latex="$ \large{U_{envelope} = \frac{\sum A_i \cdot U_i}{\sum A_i}} $" />
            </p>
          <Space align="center">
            <p style={{ margin: 0, fontSize: "18px", color: "#1890ff", fontWeight: "bold" }}>
              {(() => {
                const totalArea = roofArea + wallArea + windowArea;
                if (totalArea > 0) {
                  const weightedU = (roofArea * roofU + wallArea * wallU + windowArea * windowU) / totalArea;
                  return `${weightedU.toFixed(2)}`;
                }
                return "0.000";
              })()}
            </p>
            W/m²K
           
          </Space>
          
        </div>
        <Button type="primary" size="small" onClick={handleConfirm}>
          {t('common.confirm')}
        </Button>
      </Space>
    </Drawer>
  );
};

UParametersModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  roofArea: PropTypes.number.isRequired,
  roofU: PropTypes.number.isRequired,
  wallArea: PropTypes.number.isRequired,
  wallU: PropTypes.number.isRequired,
  windowArea: PropTypes.number.isRequired,
  windowU: PropTypes.number.isRequired,
  onRoofAreaChange: PropTypes.func.isRequired,
  onRoofUChange: PropTypes.func.isRequired,
  onWallAreaChange: PropTypes.func.isRequired,
  onWallUChange: PropTypes.func.isRequired,
  onWindowAreaChange: PropTypes.func.isRequired,
  onWindowUChange: PropTypes.func.isRequired,
  onEnvelopeUChange: PropTypes.func.isRequired,
};

/**
 * Radiator Types Information Modal
 * Displays radiator types reference image
 */
export const RadiatorTypesModal = ({ visible, onClose }) => {
  const { t } = useTranslation();
  
  return (
    <Drawer
      title={<span style={{ fontSize: '18px', fontWeight: 600, color: '#0050b3' }}>{t('heatPumpSimulation.modals.radiatorTypes.title')}</span>}
      placement="left"
      width={'100%'}
      onClose={onClose}
      open={visible}
      closeIcon={<ArrowLeftOutlined />}
      closable={true}
    >
      <img src={RadiatorTypesImage} alt={t('heatPumpSimulation.modals.radiatorTypes.title')} style={{ width: "100%", height: "97%", objectFit: "contain" }} />
    </Drawer>
  );
};

RadiatorTypesModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Document Resources Modal
 * Provides access to educational PDF and video learning resources.
 */
export const DocumentResourcesModal = ({ visible, onClose, resources }) => {
  const { t } = useTranslation();
  const resourceItems = Array.isArray(resources) && resources.length > 0
    ? resources
    : [
        {
          ...DEFAULT_LEARNING_RESOURCES[0],
          title: t('heatPumpSimulation.modals.learningResources.fundamentals'),
        },
        {
          ...DEFAULT_LEARNING_RESOURCES[1],
          title: t('heatPumpSimulation.modals.learningResources.uValues'),
        },
        {
          ...DEFAULT_LEARNING_RESOURCES[2],
          title: t('heatPumpSimulation.modals.learningResources.ventilation'),
        },
      ];

  return (
    <Drawer
      title={<span style={{ fontSize: '18px', fontWeight: 600, color: '#0050b3' }}>{t('heatPumpSimulation.modals.learningResources.title')}</span>}

      placement="right"
      width={500}
      onClose={onClose}
      open={visible}
      closeIcon={<ArrowLeftOutlined />}
      closable={true}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {resourceItems.length > 0 ? resourceItems.map((resource) => {
          const presentation = getLearningResourcePresentation(resource);
          const ResourceIcon = presentation.icon;
          const href = resource.relativeUrl || resource.downloadUrl || resource.file;
          const title = resource.fileName || resource.title;

          return (
            <a
              key={resource.id || href || title}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.documentLink}
              style={{ borderColor: `${presentation.accentColor}22` }}
            >
              <div className={styles.resourceLinkMain}>
                <div
                  className={styles.resourceLinkIconWrap}
                  style={{ backgroundColor: `${presentation.accentColor}14`, color: presentation.accentColor }}
                >
                  <ResourceIcon className={styles.resourceLinkIcon} />
                </div>
                <div className={styles.resourceLinkText}>
                  <span className={styles.resourceLinkTitle}>{title}</span>
                </div>
              </div>
            </a>
          );
        }) : (
          <div className={styles.resourcesEmptyState}>
            <Empty description={t('heatPumpSimulation.modals.learningResources.empty', 'No learning resources are attached to this training profile yet.')} />
          </div>
        )}
      </Space>
    </Drawer>
  );
};

DocumentResourcesModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  resources: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fileName: PropTypes.string,
    title: PropTypes.string,
    file: PropTypes.string,
    relativeUrl: PropTypes.string,
    downloadUrl: PropTypes.string,
    contentType: PropTypes.string,
    fileExtension: PropTypes.string,
  })),
};

/**
 * Building Era Selection Modal
 * Allows users to select a building era preset with corresponding U-values
 */
export const BuildingEraModal = ({ visible, onClose, onSelectUValue, currentUValue }) => {
  const { t } = useTranslation();
  
  const BUILDING_ERAS_TRANSLATED = [
    {
      year: "1960",
      title: t('heatPumpSimulation.modals.buildingEra.pre1970.title'),
      subtitle: t('heatPumpSimulation.modals.buildingEra.pre1970.subtitle'),
      description: t('heatPumpSimulation.modals.buildingEra.pre1970.description'),
      uValue: 1.8,
      image: Building1960,
    },
    {
      year: "1980",
      title: t('heatPumpSimulation.modals.buildingEra.1970s1990s.title'),
      subtitle: t('heatPumpSimulation.modals.buildingEra.1970s1990s.subtitle'),
      description: t('heatPumpSimulation.modals.buildingEra.1970s1990s.description'),
      uValue: 1.2,
      image: Building1980,
    },
    {
      year: "2000",
      title: t('heatPumpSimulation.modals.buildingEra.2000s.title'),
      subtitle: t('heatPumpSimulation.modals.buildingEra.2000s.subtitle'),
      description: t('heatPumpSimulation.modals.buildingEra.2000s.description'),
      uValue: 0.6,
      image: Building2000,
    },
    {
      year: "2020",
      title: t('heatPumpSimulation.modals.buildingEra.passiveHouse.title'),
      subtitle: t('heatPumpSimulation.modals.buildingEra.passiveHouse.subtitle'),
      description: t('heatPumpSimulation.modals.buildingEra.passiveHouse.description'),
      uValue: 0.18,
      image: Building2020,
    },
  ];
  
  const handleCardClick = (era) => {
    onSelectUValue(era.uValue);
    onClose();
  };

  return (
    <Drawer
      title={
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: '18px', fontWeight: 600, color: '#0050b3' }}>{t('heatPumpSimulation.modals.buildingEra.title')}</span>
          <span style={{ fontSize: '12px', color: '#666', fontWeight: 400 }}>
            {t('heatPumpSimulation.modals.buildingEra.subtitle')}
          </span>
        </Space>
      }
      placement="left"
      width={'60%'}
      onClose={onClose}
      open={visible}
      closeIcon={<ArrowLeftOutlined />}
      closable={true}
    >
      <div className={styles.buildingEraGrid}>
        {BUILDING_ERAS_TRANSLATED.map((era) => (
          <div
            key={era.year}
            className={`${styles.buildingEraCard}`}
            onClick={() => handleCardClick(era)}
          >
            {/* Image Container */}
            <div className={styles.buildingEraImageContainer}>
              <img
                src={era.image}
                alt={`Building from ${era.year}`}
                className={styles.buildingEraImage}
              />
              <div className={styles.buildingEraOverlay}>
                <p className={styles.buildingEraTitle}>{era.title}</p>
              </div>
              {Math.abs(currentUValue - era.uValue) < 0.01 && (
                <CheckCircleFilled 
                  style={{ 
                    position: 'absolute', 
                    top: 12, 
                    right: 12, 
                    fontSize: 24, 
                    color: '#52c41a',
                    background: 'white',
                    borderRadius: '50%'
                  }} 
                />
              )}
            </div>

            {/* Info Section */}
            <div className={styles.buildingEraInfo}>
              <div className={styles.buildingEraUValue}>
                <div>
                  <p className={styles.buildingEraSubtitle}>{era.subtitle}</p>
                  <p className={styles.buildingEraDescription}>{era.description}</p>
                </div>
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginLeft: '10px' }}>
                  <span className={styles.buildingEraUNumber}>{era.uValue}</span>
                  <LatexRenderer latex="$ \frac{W}{m^2 \cdot K} $" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ 
        marginTop: 24, 
        padding: 16, 
        background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)', 
        borderRadius: 8,
        borderLeft: '4px solid #1890ff'
      }}>
        <Space direction="vertical" size={4}>
          <p style={{ margin: 0, fontWeight: 600, color: '#0050b3' }}>
            💡 {t('heatPumpSimulation.modals.buildingEra.understanding')}
          </p>
          <p style={{ margin: 0, fontSize: 13 }}>
            {t('heatPumpSimulation.modals.buildingEra.understandingText')}
          </p>
        </Space>
      </div>
    </Drawer>
  );
};

BuildingEraModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectUValue: PropTypes.func.isRequired,
  currentUValue: PropTypes.number.isRequired,
};

// Fix for default marker icon in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom red marker for selected location
const selectedMarkerIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

/**
 * Map click handler component with crosshair cursor
 */
const MapClickHandler = ({ onLocationSelect }) => {
  const map = useMap();
  
  useEffect(() => {
    // Add crosshair cursor to map container
    const container = map.getContainer();
    container.style.cursor = 'crosshair';
    
    return () => {
      container.style.cursor = '';
    };
  }, [map]);
  
  useMapEvents({
    click: (e) => {
      onLocationSelect([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
};

/**
 * Component to recenter map and fix size when drawer opens
 */
const MapRecenter = ({ position, visible }) => {
  const map = useMap();
  
  useEffect(() => {
    // Invalidate size when drawer becomes visible to fix rendering
    if (visible) {
      setTimeout(() => {
        map.invalidateSize();
      }, 300);
    }
  }, [visible, map]);
  
  useEffect(() => {
    if (position) {
      map.setView(position, Math.max(map.getZoom(), 8));
    }
  }, [position, map]);
  
  return null;
};

/**
 * Map Location Modal
 * Allows users to select a location from a map and get current temperature
 */
export const MapLocationModal = ({
  visible,
  onClose,
  currentPosition,
  onLocationSelect,
  onTemperatureUpdate,
}) => {
  const { t } = useTranslation();
  const [selectedPosition, setSelectedPosition] = useState(currentPosition);
  const [locationName, setLocationName] = useState("");
  const [temperature, setTemperature] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gettingCurrentLocation, setGettingCurrentLocation] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setSelectedPosition(currentPosition);
      setTemperature(null);
      setLocationName("");
    }
  }, [visible, currentPosition]);

  // Fetch location name using reverse geocoding
  const fetchLocationName = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      if (response.ok) {
        const data = await response.json();
        const city = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality;
        const country = data.address?.country;
        return city ? `${city}, ${country}` : country || t('heatPumpSimulation.modals.mapLocation.unknownLocation');
      }
    } catch (error) {
      console.error("Error fetching location name:", error);
    }
    return t('heatPumpSimulation.modals.mapLocation.unknownLocation');
  };

  // Fetch temperature for selected location
  const fetchTemperature = async (lat, lng) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m`
      );
      if (response.ok) {
        const data = await response.json();
        setTemperature(data.current.temperature_2m);
      }
      const name = await fetchLocationName(lat, lng);
      setLocationName(name);
    } catch (error) {
      console.error("Error fetching temperature:", error);
      message.error(t('heatPumpSimulation.modals.mapLocation.failedToFetch'));
    } finally {
      setLoading(false);
    }
  };

  // Handle location selection on map
  const handleLocationSelect = (position) => {
    setSelectedPosition(position);
    fetchTemperature(position[0], position[1]);
  };

  // Get current location using browser geolocation
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      message.error(t('heatPumpSimulation.modals.mapLocation.geolocationNotSupported'));
      return;
    }

    setGettingCurrentLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newPos = [position.coords.latitude, position.coords.longitude];
        setSelectedPosition(newPos);
        fetchTemperature(newPos[0], newPos[1]);
        setGettingCurrentLocation(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        message.error(t('heatPumpSimulation.modals.mapLocation.unableToGetLocation'));
        setGettingCurrentLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Confirm selection and update parent
  const handleConfirm = () => {
    if (selectedPosition && temperature !== null) {
      onLocationSelect(selectedPosition);
      onTemperatureUpdate(temperature);
      onClose();
    }
  };

  return (
    <Drawer
      title={
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: '18px', fontWeight: 600, color: '#0050b3' }}>
            <EnvironmentOutlined /> {t('heatPumpSimulation.modals.mapLocation.title')}
          </span>
          <span style={{ fontSize: '12px', color: '#666', fontWeight: 400 }}>
            {t('heatPumpSimulation.modals.mapLocation.subtitle')}
          </span>
        </Space>
      }
      placement="left"
      width={'60%'}
      onClose={onClose}
      open={visible}
      closeIcon={<ArrowLeftOutlined />}
      closable={true}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Current Location Button */}
        <Button
          type="primary"
          icon={<AimOutlined />}
          onClick={getCurrentLocation}
          loading={gettingCurrentLocation}
          style={{ marginBottom: 8 }}
        >
          {t('heatPumpSimulation.modals.mapLocation.useCurrentLocation')}
        </Button>

        {/* Map Container */}
        <div style={{ 
          height: '450px', 
          width: '100%', 
          borderRadius: '8px', 
          overflow: 'hidden', 
          border: '2px solid #1890ff',
          boxShadow: '0 4px 12px rgba(24, 144, 255, 0.15)'
        }}>
          {visible && (
            <MapContainer
              center={selectedPosition || [50.77664, 6.08342]}
              zoom={5}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              doubleClickZoom={true}
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapClickHandler onLocationSelect={handleLocationSelect} />
              <MapRecenter position={selectedPosition} visible={visible} />
              {selectedPosition && <Marker position={selectedPosition} icon={selectedMarkerIcon} />}
            </MapContainer>
          )}
        </div>

        {/* Location Info */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <p style={{ marginTop: 8, color: '#666' }}>{t('heatPumpSimulation.modals.mapLocation.fetchingWeather')}</p>
          </div>
        ) : selectedPosition && temperature !== null ? (
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)',
            borderRadius: '8px',
            borderLeft: '4px solid #1890ff'
          }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space>
                <EnvironmentOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                <span style={{ fontWeight: 600, color: '#0050b3' }}>{locationName}</span>
              </Space>
              <Space>
                <span style={{ color: '#666' }}>{t('heatPumpSimulation.modals.mapLocation.coordinates')}:</span>
                <span style={{ fontFamily: 'monospace' }}>
                  {selectedPosition[0].toFixed(4)}°, {selectedPosition[1].toFixed(4)}°
                </span>
              </Space>
              <Space>
                <span style={{ color: '#666' }}>{t('heatPumpSimulation.modals.mapLocation.currentTemperature')}:</span>
                <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                  {temperature.toFixed(1)} °C
                </span>
              </Space>
            </Space>
          </div>
        ) : (
          <div style={{
            padding: '16px',
            background: '#fafafa',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#999'
          }}>
            {t('heatPumpSimulation.modals.mapLocation.clickToSelect')}
          </div>
        )}

        {/* Confirm Button */}
        <Button
          type="primary"
          size="large"
          block
          disabled={!selectedPosition || temperature === null}
          onClick={handleConfirm}
        >
          {t('heatPumpSimulation.modals.mapLocation.useThisTemperature')} ({temperature !== null ? `${temperature.toFixed(1)} °C` : '--'})
        </Button>
      </Space>
    </Drawer>
  );
};

MapLocationModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentPosition: PropTypes.arrayOf(PropTypes.number).isRequired,
  onLocationSelect: PropTypes.func.isRequired,
  onTemperatureUpdate: PropTypes.func.isRequired,
};
